import { useEffect, useState, useMemo } from "react";
import { api, formatErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";

export default function AdminAttendance() {
  const [employees, setEmployees] = useState([]);
  const [todayStatus, setTodayStatus] = useState([]);
  const [report, setReport] = useState([]);
  const [empFilter, setEmpFilter] = useState("all");
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
    loadToday();
    fetchReport();
    // eslint-disable-next-line
  }, []);

  const loadToday = () => api.get("/admin/attendance/today-status").then((r) => setTodayStatus(r.data)).catch(() => {});

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (empFilter && empFilter !== "all") params.append("employee_id", empFilter);
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      const { data } = await api.get(`/admin/attendance/report?${params.toString()}`);
      setReport(data);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };

  const exportCSV = () => {
    if (report.length === 0) { toast.error("No data to export"); return; }
    const headers = ["Date", "Employee ID", "Employee Name", "Clock In", "Clock Out", "Working Hours"];
    const rows = report.map((r) => [
      r.date,
      r.employee_id,
      r.full_name || "",
      r.login_time || "",
      r.logout_time || "",
      r.working_hours || 0,
    ]);
    const csv = [headers, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_${fromDate}_to_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported");
  };

  const counts = useMemo(() => {
    const c = { logged_in: 0, completed: 0, absent: 0 };
    todayStatus.forEach((s) => { c[s.status] = (c[s.status] || 0) + 1; });
    return c;
  }, [todayStatus]);

  const statusBadge = (s) => ({
    logged_in: "bg-emerald-50 text-emerald-700 border-emerald-300",
    completed: "bg-[#4F46E5]/10 text-[#4F46E5] border-[#4F46E5]/30",
    absent: "bg-red-50 text-red-700 border-red-300",
  }[s] || "bg-zinc-100 dark:bg-zinc-800");

  const fmt = (iso) => iso ? new Date(iso).toLocaleTimeString("en-GB") : "—";

  return (
    <div className="space-y-6" data-testid="admin-attendance-page">
      <div>
        <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-600 mb-2">02 / Workforce Log</div>
        <h1 className="rule-text text-3xl sm:text-4xl text-zinc-900 dark:text-zinc-100">Attendance.</h1>
      </div>

      <Tabs defaultValue="today" className="space-y-4">
        <TabsList className="rounded-xl bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm p-0 h-auto">
          <TabsTrigger value="today" className="rounded-xl data-[state=active]:bg-zinc-950 data-[state=active]:text-white uppercase tracking-[0.18em] text-xs font-bold px-6 py-3" data-testid="tab-today">Today</TabsTrigger>
          <TabsTrigger value="report" className="rounded-xl data-[state=active]:bg-zinc-950 data-[state=active]:text-white uppercase tracking-[0.18em] text-xs font-bold px-6 py-3" data-testid="tab-report">Report</TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatBox label="Logged In" value={counts.logged_in} accent="border-emerald-300 bg-emerald-50 text-emerald-700" />
            <StatBox label="Completed" value={counts.completed} accent="border-[#4F46E5] bg-[#4F46E5]/5 text-[#4F46E5]" />
            <StatBox label="Absent" value={counts.absent} accent="border-red-300 bg-red-50 text-red-700" />
          </div>
          <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm overflow-x-auto">
          <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">ID</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Name</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Type</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Clock In</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Clock Out</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Hours</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {todayStatus.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-zinc-500 dark:text-zinc-400 text-sm">No employees yet.</TableCell></TableRow>
                ) : todayStatus.map((s) => (
                  <TableRow key={s.employee_id} data-testid={`today-row-${s.employee_id}`}>
                    <TableCell className="font-mono text-xs">{s.employee_id}</TableCell>
                    <TableCell className="font-bold">{s.full_name}</TableCell>
                    <TableCell><span className="eyebrow bg-zinc-100 dark:bg-zinc-800 px-2 py-1 border border-zinc-200 dark:border-zinc-800">{s.employee_type}</span></TableCell>
                    <TableCell className="font-mono">{fmt(s.login_time)}</TableCell>
                    <TableCell className="font-mono">{fmt(s.logout_time)}</TableCell>
                    <TableCell className="font-mono font-bold">{s.working_hours || 0}</TableCell>
                    <TableCell><span className={`eyebrow px-2 py-1 border ${statusBadge(s.status)}`}>{s.status.replace("_", " ")}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="report" className="space-y-4">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 items-end">
            <div>
              <Label className="eyebrow">Employee</Label>
              <Select value={empFilter} onValueChange={setEmpFilter}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="report-emp-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.full_name} ({e.employee_id})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow">From Date</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rounded-xl mt-1" data-testid="report-from-date" />
            </div>
            <div>
              <Label className="eyebrow">To Date</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rounded-xl mt-1" data-testid="report-to-date" />
            </div>
            <Button onClick={fetchReport} disabled={loading} className="klein-btn rounded-xl uppercase tracking-[0.2em] text-xs font-bold h-10" data-testid="apply-filters-btn">
              <Search size={14} className="mr-2" /> Apply
            </Button>
            <Button onClick={exportCSV} variant="outline" className="rounded-xl uppercase tracking-[0.2em] text-xs font-bold h-10 border-zinc-300" data-testid="export-csv-btn">
              <Download size={14} className="mr-2" /> Export CSV
            </Button>
          </div>
          <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm overflow-x-auto">
          <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Date</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Employee</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Clock In</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Clock Out</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Hours</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-zinc-500 dark:text-zinc-400 text-sm">No records for selected filters.</TableCell></TableRow>
                ) : report.map((r) => (
                  <TableRow key={`${r.employee_id}-${r.date}`} data-testid={`report-row-${r.employee_id}-${r.date}`}>
                    <TableCell className="font-mono">{r.date}</TableCell>
                    <TableCell className="font-bold">{r.full_name}<div className="text-xs text-zinc-500 dark:text-zinc-400 font-normal">{r.employee_id}</div></TableCell>
                    <TableCell className="font-mono">{fmt(r.login_time)}</TableCell>
                    <TableCell className="font-mono">{fmt(r.logout_time)}</TableCell>
                    <TableCell className="font-mono font-bold">{r.working_hours || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const StatBox = ({ label, value, accent }) => (
  <div className={`border p-6 ${accent}`} data-testid={`today-stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
    <div className="eyebrow opacity-80">{label}</div>
    <div className="rule-text text-4xl mt-2">{value}</div>
  </div>
);
